/**
 * Email Verification Page
 *
 * Shows when user hasn't verified their email yet.
 * Provides:
 * - Clear instructions
 * - Resend verification email button
 * - Auto-redirect once email is verified
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ContentSageIcon } from '../components/ContentSageLogo';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, resendVerificationEmail, signOutUser, refreshUser } = useAuth();

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(false);

  // Get the redirect path from location state, default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Poll Firebase for the verification flag.
  //
  // This used to sleep for a second and then re-read `user.emailVerified` off
  // the context snapshot. That could never change value: the snapshot is a
  // plain object, and `onAuthStateChanged` does not fire when a verification
  // link is clicked - `emailVerified` is cached in the ID token. The page
  // therefore polled forever while Firebase had already recorded the user as
  // verified. Only `refreshUser()` actually refetches it.
  //
  // Navigation is deliberately NOT done here. Once the refreshed snapshot
  // lands, the effect below reacts to it - and that is the same value
  // ProtectedRoute gates on, so we can never navigate somewhere that bounces
  // us straight back.
  useEffect(() => {
    if (user?.emailVerified) return;

    const checkInterval = setInterval(async () => {
      setCheckingVerification(true);
      try {
        await refreshUser();
      } finally {
        setCheckingVerification(false);
      }
    }, 3000);

    return () => clearInterval(checkInterval);
  }, [user?.emailVerified, refreshUser]);

  // If user is already verified, redirect
  useEffect(() => {
    if (user?.emailVerified) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const handleResendEmail = async () => {
    if (!user || sending) return;

    setSending(true);
    setError(null);
    setSent(false);

    try {
      await resendVerificationEmail();
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSignOut = async () => {
    await signOutUser();
    navigate('/login');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <ContentSageIcon size={40} />
          <span className="text-2xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-amber-500 bg-clip-text text-transparent">
            ContentSage
          </span>
        </div>

        <div className="bg-white py-12 px-6 shadow-xl rounded-2xl border border-gray-100">
          {/* Icon */}
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-purple-600" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Verify Your Email
          </h2>

          {/* Email Address */}
          <p className="text-center text-gray-600 mb-6">
            We sent a verification email to:
          </p>
          <p className="text-center font-semibold text-gray-900 mb-6">
            {user.email}
          </p>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900 mb-2">
              <strong>Next steps:</strong>
            </p>
            <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
              <li>Check your inbox for the verification email</li>
              <li>Click the verification link in the email</li>
              <li>This page will automatically refresh</li>
            </ol>
          </div>

          {/* Auto-check status */}
          {checkingVerification && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Checking verification status...</span>
            </div>
          )}

          {/* Success Message */}
          {sent && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3"
            >
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-900">Email sent!</p>
                <p className="text-sm text-green-700 mt-1">
                  Check your inbox (and spam folder) for the verification email.
                </p>
              </div>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Resend Button */}
          <button
            onClick={handleResendEmail}
            disabled={sending || sent}
            className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            {sending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : sent ? (
              <>
                <CheckCircle className="w-5 h-5" />
                Email Sent
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Resend Verification Email
              </>
            )}
          </button>

          {/* Help Text */}
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              Didn't receive the email? Check your spam folder or try resending.
            </p>

            {/* Sign Out Link */}
            <button
              onClick={handleSignOut}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Sign out and use a different email
            </button>
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need help?{' '}
            <a
              href="mailto:support@contentsage.ai"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Contact support
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
